/** Pure helpers for the period drill-down: time-of-day P&L buckets (the
 * pie), VIX-vs-P&L pairing (the scatter), and period→date-range mapping.
 * All operate on the existing exit-comparison trade + VIX data — no new
 * backend. Kept DOM-free so the math is unit-tested. */

import type { Granularity, TradeComparison, VixDay } from "@/lib/reportsApi";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** Weekday (Mon/Tue/…) for an IST calendar date "YYYY-MM-DD". */
export function weekdayOf(date: string): string {
  return WEEKDAYS[new Date(`${date}T00:00:00Z`).getUTCDay()] ?? "";
}

export interface DayBar {
  date: string; // YYYY-MM-DD
  dayLabel: string; // day-of-month
  weekday: string; // Mon/Tue/…
  pnl: number;
  vix: number | null;
}

/** Per-day net LIVE P&L for a month, each paired with its weekday and
 * that day's VIX close. Ascending by date, only days that traded. */
export function dailyPnlWithVix(
  trades: readonly TradeComparison[],
  vix: readonly VixDay[],
): DayBar[] {
  const pnlByDay = new Map<string, number>();
  for (const t of trades) {
    if (t.live.pnl === null) continue;
    const d = t.report_at.slice(0, 10);
    pnlByDay.set(d, (pnlByDay.get(d) ?? 0) + t.live.pnl);
  }
  const vixByDay = new Map(vix.map((v) => [v.trade_date, v.close] as const));
  return [...pnlByDay.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, pnl]) => ({
      date,
      dayLabel: String(Number(date.slice(8, 10))),
      weekday: weekdayOf(date),
      pnl,
      vix: vixByDay.get(date) ?? null,
    }));
}

/** Compact bar label for a timeseries period key: a day key (YYYY-MM-DD)
 * shows the day-of-month; a month key (YYYY-MM) shows the month name. */
export function shortPeriodLabel(period: string): string {
  const parts = period.split("-");
  if (parts.length === 3) return String(Number(parts[2])); // day-of-month
  if (parts.length === 2) return MONTHS[Number(parts[1]) - 1] ?? period;
  return period;
}

const SESSION_OPEN_MIN = 9 * 60 + 15; // 09:15 IST
const SESSION_CLOSE_MIN = 15 * 60 + 30; // 15:30 IST

/** Minutes since IST midnight for an ISO (UTC) timestamp — pure, no
 * Intl/timezone DB needed (IST is a fixed +5:30 offset). */
export function istMinutesOfDay(iso: string): number {
  const d = new Date(iso);
  return (d.getUTCHours() * 60 + d.getUTCMinutes() + 330) % 1440;
}

function slotLabel(minsSinceMidnight: number): string {
  const h = Math.floor(minsSinceMidnight / 60);
  const m = minsSinceMidnight % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export interface TodBucket {
  label: string; // HH:MM slot start (IST)
  pnl: number;
  trades: number;
}

/** P&L grouped by the entry time-of-day slot (slotMin = 5 | 15 | 60).
 * Trades before the open / after close clamp into the edge slots. Only
 * buckets with trades are returned, in session order. */
export function timeOfDayBuckets(trades: readonly TradeComparison[], slotMin: number): TodBucket[] {
  const byStart = new Map<number, TodBucket>();
  for (const t of trades) {
    if (t.live.pnl === null) continue;
    const mins = clamp(istMinutesOfDay(t.entry_at), SESSION_OPEN_MIN, SESSION_CLOSE_MIN - 1);
    const start = SESSION_OPEN_MIN + Math.floor((mins - SESSION_OPEN_MIN) / slotMin) * slotMin;
    const b = byStart.get(start) ?? { label: slotLabel(start), pnl: 0, trades: 0 };
    b.pnl += t.live.pnl;
    b.trades += 1;
    byStart.set(start, b);
  }
  return [...byStart.entries()].sort(([a], [b]) => a - b).map(([, b]) => b);
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/** [from, to] IST calendar dates for a selected period key. */
export function periodRange(
  period: string,
  granularity: Granularity,
): { from: string; to: string } {
  if (granularity === "day") return { from: period, to: period };
  if (granularity === "month") {
    const [y, m] = period.split("-").map(Number);
    const last = new Date(Date.UTC(y ?? 0, m ?? 1, 0)).getUTCDate();
    return { from: `${period}-01`, to: `${period}-${String(last).padStart(2, "0")}` };
  }
  return { from: `${period}-01-01`, to: `${period}-12-31` };
}
